import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, Flame, Gamepad2, Store } from "lucide-react";
import { CampaignCard } from "@/components/public/CampaignCard";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import IdChipPicker from "@/components/franchise/IdChipPicker";

export default function FranchiseHomepageEditorPage() {
  const { admin } = useAdminAuth();
  const city = admin?.assigned_city || "";
  const [content, setContent] = useState(null);
  const [banner, setBanner] = useState("");
  const [bannerUploading, setBannerUploading] = useState(false);
  // Arrays of ids — the pickers manage add/remove; save stores them
  // as-is, matching the underlying schema (curated_offer_campaign_ids,
  // logo_section_vendor_ids). This is a UI-only change from the
  // previous "one id per line" textarea.
  const [campaignIds, setCampaignIds] = useState([]);
  const [vendorIds, setVendorIds] = useState([]);
  // Per-city display caps (P3.2) — nullable ints. Empty string in
  // the input represents "inherit" (leave unset); an actual number
  // overrides the global default. Kept as strings for the form,
  // converted to Number|null at save time.
  const [caps, setCaps] = useState({ trending: "", offers: "", stores: "" });
  const [busy, setBusy] = useState(false);

  // ── Preview state ──────────────────────────────────────────────
  // "Preview as visitor" resolves the current (unsaved) draft against
  // live campaign + vendor data via the same public homepage
  // enrichment endpoint the real homepage consumes. We do this by
  // hitting the LIVE endpoint (server payload for the SAVED record)
  // and then, if the draft diverges from what's saved, augmenting the
  // response client-side by fetching each newly-added campaign /
  // vendor by id. This gives the curator an accurate preview WITHOUT
  // needing a separate /preview endpoint or committing the draft.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // Toggle for the hero cover in the preview modal — lets the
  // curator see the campaign strip without scrolling past the cover
  // image, without touching the actual saved value.
  const [previewHideCover, setPreviewHideCover] = useState(false);

  const draftCampaignIds = useMemo(() => campaignIds.filter(Boolean), [campaignIds]);
  const draftVendorIds = useMemo(() => vendorIds.filter(Boolean), [vendorIds]);

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      // Start from the live payload so campaign/vendor hydration
      // (thumbnail, colors, badges) matches homepage exactly.
      const r = await api.publicCityHomepage(city);
      const live = r.data || {};
      // Which draft ids AREN'T already in the live payload? Fetch
      // those separately so the preview reflects the unsaved list.
      const liveCampaignIds = new Set((live.curated_campaigns || []).map((c) => c.id));
      const liveVendorIds = new Set((live.logo_vendors || []).map((v) => v.id));
      const missingC = draftCampaignIds.filter((id) => !liveCampaignIds.has(id));
      const missingV = draftVendorIds.filter((id) => !liveVendorIds.has(id));

      // Fetch missing campaigns via the public offer detail — the
      // static_offer endpoint returns a payload that maps cleanly to
      // the curated card shape. Missing spin/scratch campaigns fall
      // back to the /api/game/{id} shape.
      const extraCampaigns = [];
      for (const id of missingC) {
        try {
          const cr = await api.publicCampaign(id);
          const d = cr.data || {};
          extraCampaigns.push({
            id: d.id, name: d.name, game_type: d.game_type,
            thumbnail_url: d.thumbnail_url, logo_url: d.logo_url,
            primary_color: d.primary_color, secondary_color: d.secondary_color,
            vendor_id: d.vendor_id,
            is_verified: !!d.is_verified, is_trusted: !!d.is_trusted,
            expires_at: d.expires_at,
          });
        } catch {
          extraCampaigns.push({ id, name: `#${id.slice(0, 8)}`, __missing: true });
        }
      }
      // Preserve draft ordering exactly (both saved AND missing)
      const byId = new Map();
      (live.curated_campaigns || []).forEach((c) => byId.set(c.id, c));
      extraCampaigns.forEach((c) => byId.set(c.id, c));
      const orderedCampaigns = draftCampaignIds.map((id) => byId.get(id)).filter(Boolean);

      // Vendors: reuse the same trick — enrich missing ones best-effort.
      const orderedVendors = draftVendorIds.map((id) => {
        const found = (live.logo_vendors || []).find((v) => v.id === id);
        return found || { id, business: `#${id.slice(0, 8)}`, __missing: true };
      });
      setPreviewData({
        city,
        cover_banner_url: banner.trim() || null,
        curated_campaigns: orderedCampaigns,
        logo_vendors: orderedVendors,
      });
    } catch (e) {
      toast.error("Preview failed");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!city) return;
    api.publicCityHomepage(city).then((r) => {
      const d = r.data || {};
      setContent(d);
      setBanner(d.cover_banner_url || "");
      // Reconstruct id lists for editing — arrays now, not
      // newline-separated strings.
      setCampaignIds((d.curated_campaigns || []).map((c) => c.id));
      setVendorIds((d.logo_vendors || []).map((v) => v.id));
      // Load current per-city caps (null → "" means "inherit").
      setCaps({
        trending: d.trending_display_count == null ? "" : String(d.trending_display_count),
        offers:   d.offers_display_count   == null ? "" : String(d.offers_display_count),
        stores:   d.stores_display_count   == null ? "" : String(d.stores_display_count),
      });
    });
  }, [city]);

  // Sub-admins can't edit homepage content (Part 8c)
  if (admin?.role === "franchise_staff") {
    return <div className="p-6"><Card className="p-6">Only your franchise owner can edit the city homepage content.</Card></div>;
  }

  const lockedByAdmin = content?.last_edited_by_role === "admin" || content?.last_edited_by_role === "superadmin";
  const isFranchise = admin?.role === "franchise";
  const locked = isFranchise && lockedByAdmin;

  const save = async () => {
    setBusy(true);
    try {
      const toInt = (s) => {
        if (s == null || String(s).trim() === "") return null;
        const n = Number(s);
        return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
      };
      const payload = {
        cover_banner_url: banner.trim() || null,
        // Arrays already — send them directly; save contract unchanged.
        curated_offer_campaign_ids: campaignIds.filter(Boolean),
        logo_section_vendor_ids: vendorIds.filter(Boolean),
        // Per-city display caps — null = inherit global default.
        trending_display_count: toInt(caps.trending),
        offers_display_count:   toInt(caps.offers),
        stores_display_count:   toInt(caps.stores),
      };
      await api.adminUpsertCityHomepage(city, payload);
      toast.success("City homepage saved");
      const r = await api.publicCityHomepage(city);
      setContent(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (!city) return <div className="p-6"><Card className="p-6">No city assigned to your account.</Card></div>;

  return (
    <div className="space-y-4" data-testid="franchise-homepage-editor">
      <div>
        <h1 className="text-2xl font-semibold font-heading">City Homepage — {city}</h1>
        <p className="text-sm text-muted-foreground">Curated content renders as &ldquo;Featured in {city}&rdquo; above Trending Offers on the homepage.</p>
      </div>
      {locked && (
        <Card className="p-3 border-destructive bg-destructive/10 text-destructive text-sm">
          Locked by Admin — only an Admin/Super Admin can update from now on.
        </Card>
      )}
      {content?.last_edited_by_role && !locked && (
        <div className="text-xs text-muted-foreground">Last edited by <Badge variant="secondary" className="ml-1">{content.last_edited_by_role}</Badge></div>
      )}
      <Card className="p-4 space-y-4">
        <div className="block space-y-2">
          <span className="text-xs uppercase tracking-widest">Cover banner</span>
          <p className="text-[11px] text-muted-foreground">
            Upload an image (JPG/PNG/WebP, any reasonable size — recommended landscape ~1920×1080 but not enforced) OR paste a hosted URL. Both paths render identically.
          </p>
          <div className="flex flex-col md:flex-row gap-2 items-start">
            <label className={`mkt-btn cursor-pointer !text-sm !py-2 !px-3 whitespace-nowrap ${locked ? "opacity-50 !cursor-not-allowed" : ""}`} data-testid="homepage-banner-upload-label">
              {bannerUploading ? "Uploading…" : "Upload image"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                disabled={locked || bannerUploading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  // Relaxed guardrails — reject only egregious cases;
                  // do NOT hard-lock aspect ratio or exact pixels.
                  if (file.size > 8 * 1024 * 1024) {
                    toast.error("Image is over 8 MB — please compress and try again.");
                    return;
                  }
                  setBannerUploading(true);
                  try {
                    const r = await api.upload(file);
                    const url = r.data?.url || r.data?.image_url || r.data;
                    if (typeof url !== "string" || !url) {
                      throw new Error("Upload succeeded but no URL was returned.");
                    }
                    setBanner(url);
                    toast.success("Cover banner uploaded.");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || err?.message || "Upload failed.");
                  } finally {
                    setBannerUploading(false);
                  }
                }}
                data-testid="homepage-banner-upload"
              />
            </label>
            <Input
              value={banner}
              onChange={(e) => setBanner(e.target.value)}
              placeholder="…or paste https://…/hero.png"
              className="flex-1"
              data-testid="homepage-banner-url"
              disabled={locked}
            />
          </div>
          {banner && (
            <img
              src={banner}
              alt="Cover banner preview"
              className="mt-2 rounded-lg border-2 border-border w-full max-w-lg"
              data-testid="homepage-banner-preview"
            />
          )}
        </div>
        <div className="block space-y-2">
          <span className="text-xs uppercase tracking-widest flex items-center gap-1.5">
            <Gamepad2 className="h-3.5 w-3.5" /> Curated campaigns
          </span>
          <p className="text-[11px] text-muted-foreground">
            Search by name — results are scoped to {city}. Click to add. Drag chips to reorder is not yet supported; remove & re-add to change order.
          </p>
          <IdChipPicker
            value={campaignIds}
            onChange={setCampaignIds}
            fetchFn={(q) => api.franchiseSearchCampaigns(q)}
            hydrateFn={async (ids) => {
              // Fetch each id via the search endpoint (empty query
              // returns the most recent 50 — good enough for post-
              // load chip hydration; individual fallback below).
              const list = [];
              for (const id of ids) {
                try {
                  const r = await api.franchiseSearchCampaigns(id);
                  const hit = (r.data?.items || []).find((x) => x.id === id);
                  if (hit) list.push(hit);
                  else list.push({ id, name: `#${id.slice(0, 8)}`, __missing: true });
                } catch { list.push({ id, name: `#${id.slice(0, 8)}`, __missing: true }); }
              }
              return list;
            }}
            renderItem={(it) => (
              <div className="flex items-center gap-3">
                {it.thumbnail_url || it.logo_url ? (
                  <img src={it.thumbnail_url || it.logo_url} alt="" className="h-8 w-8 rounded object-cover border border-border" />
                ) : (
                  <div className="h-8 w-8 rounded bg-secondary flex items-center justify-center">
                    <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{it.name || `#${it.id.slice(0, 8)}`}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {it.vendor_business || "—"} · {it.game_type?.replace("_", " ")}
                  </p>
                </div>
                {(it.is_verified || it.is_trusted) && (
                  <div className="flex gap-1">
                    {it.is_verified && <Badge variant="outline" className="text-[9px]">Verified</Badge>}
                    {it.is_trusted && <Badge variant="outline" className="text-[9px]">Trusted</Badge>}
                  </div>
                )}
              </div>
            )}
            renderChip={(it) => it.name || `#${it.id.slice(0, 8)}`}
            placeholder="Search campaigns by name…"
            testIdPrefix="homepage-campaign-picker"
            disabled={locked}
          />
        </div>

        <div className="block space-y-2">
          <span className="text-xs uppercase tracking-widest flex items-center gap-1.5">
            <Store className="h-3.5 w-3.5" /> Curated vendors (logo strip)
          </span>
          <p className="text-[11px] text-muted-foreground">
            Search by business name — results are scoped to {city}.
          </p>
          <IdChipPicker
            value={vendorIds}
            onChange={setVendorIds}
            fetchFn={(q) => api.franchiseSearchVendors(q)}
            hydrateFn={async (ids) => {
              const list = [];
              for (const id of ids) {
                try {
                  const r = await api.franchiseSearchVendors(id);
                  const hit = (r.data?.items || []).find((x) => x.id === id);
                  if (hit) list.push(hit);
                  else list.push({ id, business_name: `#${id.slice(0, 8)}`, __missing: true });
                } catch { list.push({ id, business_name: `#${id.slice(0, 8)}`, __missing: true }); }
              }
              return list;
            }}
            renderItem={(it) => (
              <div className="flex items-center gap-3">
                {it.logo_url ? (
                  <img src={it.logo_url} alt="" className="h-8 w-8 rounded-full object-cover border border-border" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                    <Store className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{it.business_name || `#${it.id.slice(0, 8)}`}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {(it.business_type || "vendor").toString().replace(/_/g, " ")} · {it.city || "—"}
                  </p>
                </div>
              </div>
            )}
            renderChip={(it) => it.business_name || `#${it.id.slice(0, 8)}`}
            placeholder="Search vendors by business name…"
            testIdPrefix="homepage-vendor-picker"
            disabled={locked}
          />
        </div>

        {/* ── Per-city display caps ─────────────────────────────── */}
        <div className="block space-y-2 border-t-2 border-border pt-4">
          <span className="text-xs uppercase tracking-widest">Display caps (per section)</span>
          <p className="text-[11px] text-muted-foreground">
            Cap how many items each section shows on {city}'s homepage. Leave blank to inherit the global default. Enter <b>0</b> to hide a section entirely.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: "trending", label: "Trending offers" },
              { key: "offers",   label: "All live offers" },
              { key: "stores",   label: "Popular stores" },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Inherit"
                  value={caps[key]}
                  onChange={(e) => setCaps((c) => ({ ...c, [key]: e.target.value }))}
                  disabled={locked}
                  data-testid={`homepage-cap-${key}`}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={runPreview}
            disabled={busy || previewLoading}
            className="gap-2"
            data-testid="homepage-preview-btn"
          >
            <Eye className="h-4 w-4" />
            {previewLoading ? "Loading preview…" : "Preview as visitor"}
          </Button>
          <Button onClick={save} disabled={busy || locked} data-testid="homepage-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </Card>

      {/* ── Preview modal — renders the exact "Featured in {city}"
          section shape the homepage uses, from the UNSAVED draft. */}
      <Dialog open={previewOpen} onOpenChange={(v) => !v && setPreviewOpen(false)}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="homepage-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Preview &mdash; Featured in {city}
            </DialogTitle>
            <DialogDescription>
              Exact visitor render. This is a draft — nothing has been saved yet.
            </DialogDescription>
          </DialogHeader>
          {previewData?.cover_banner_url && (
            <div className="flex items-center justify-end gap-2 pb-2">
              <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="preview-hide-cover-label">
                <input
                  type="checkbox"
                  checked={previewHideCover}
                  onChange={(e) => setPreviewHideCover(e.target.checked)}
                  className="h-4 w-4 rounded border-2 border-border"
                  data-testid="preview-hide-cover"
                />
                Hide cover in preview
              </label>
            </div>
          )}
          {previewLoading ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
          ) : !previewData ? (
            <p className="text-sm text-muted-foreground py-10 text-center">No draft to preview.</p>
          ) : (
            <div className="rounded-2xl border-2 border-[#0A0A0A] bg-[#F7F4EC] p-4 md:p-6" style={{ boxShadow: "6px 6px 0 #0A0A0A" }}>
              <div className="mb-4">
                <p className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#F5A800] bg-[#0A0A0A] px-2 py-0.5 rounded-full">
                  <Flame className="h-3 w-3" /> Featured in {city}
                </p>
                <h2 className="mt-2 font-heading text-2xl md:text-3xl font-semibold text-[#0A0A0A]">
                  Hand-picked for {city}
                </h2>
              </div>
              {previewData.cover_banner_url && !previewHideCover && (
                <div className="rounded-2xl overflow-hidden border-2 border-[#0A0A0A] shadow-[6px_6px_0_#0A0A0A] mb-5">
                  <img
                    src={previewData.cover_banner_url}
                    alt=""
                    className="block w-full h-auto object-cover"
                  />
                </div>
              )}
              {(previewData.curated_campaigns || []).length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {previewData.curated_campaigns.map((c) => (
                    c.__missing ? (
                      <div key={c.id} className="p-4 rounded-2xl border-2 border-dashed border-destructive text-destructive text-xs">
                        Campaign <b>{c.id}</b> could not be loaded — check the ID or ensure it is live.
                      </div>
                    ) : (
                      <CampaignCard key={c.id} campaign={c} />
                    )
                  ))}
                </div>
              )}
              {(previewData.logo_vendors || []).length > 0 && (
                <div className="mt-6">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0A0A0A]/60 mb-3">
                    Featured brands
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    {previewData.logo_vendors.map((v) => (
                      <div key={v.id} className={"flex items-center gap-2 px-3 py-2 rounded-lg border-2 " + (v.__missing ? "border-dashed border-destructive text-destructive text-xs" : "border-[#0A0A0A] bg-white") }>
                        {v.logo_url && !v.__missing && <img src={v.logo_url} alt="" className="h-6 w-auto" />}
                        <span className="text-xs font-semibold uppercase tracking-widest">{v.business || v.id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(previewData.curated_campaigns || []).length === 0
                && !previewData.cover_banner_url
                && (previewData.logo_vendors || []).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-10">
                  Nothing to preview yet — add a cover banner, a curated campaign ID, or a vendor ID.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
